import { i18nNsFactory } from '@atlas-frontend/i18n';
import { RefinementCtx, z, ZodError } from 'zod/v3';
import {
  errorMap,
  objectWithRandomUniqueKey,
  zCode,
} from '../../../../../common/utils';
import translations from './AlgorithmJoinItemValidFormDataSchema.translations.json';
import { isNumberInFormat } from '../../../../../common/utils/parsers/parseNumber';

const { addTranslations, t } = i18nNsFactory(
  'algorithm-join-item-valid-form-data-schema',
);
addTranslations(translations);

const AlgorithmJoinItemBaseValidFormDataSchema = z.object({
  id: z
    .number()
    .transform((v) => v || null) // 0 -> null
    .nullable()
    .optional(),
});

export const zValue = (role: 'source' | 'target') =>
  zCode(errorMap((issue) => t(`validation.${role}.value.${issue.code}`)));

const zDimension = (role: 'source' | 'target') =>
  z
    .string(
      errorMap((issue) => t(`validation.${role}.dimension.${issue.code}`)),
    )
    .min(1);

const zAlgorithmMappingAlias = z
  .string(errorMap(() => t('validation.source.algorithmMappingAlias.required')))
  .min(1);

const zAlgorithmSourceId = z.preprocess((value) => value || 0, z.number());

const dimensionDimensionSchema =
  AlgorithmJoinItemBaseValidFormDataSchema.extend({
    _variant: z.literal('DIMENSION_DIMENSION'),
    algorithmSourceId: zAlgorithmSourceId,
    sourceDimension: zDimension('source'),
    targetDimension: zDimension('target'),
  });

const dimensionValueSchema = AlgorithmJoinItemBaseValidFormDataSchema.extend({
  _variant: z.literal('DIMENSION_VALUE'),
  algorithmSourceId: zAlgorithmSourceId,
  sourceDimension: zDimension('source'),
  targetValue: z.union([
    z.string(errorMap(() => t('validation.target.value.too_small'))).min(1),
    z.number(),
  ]),
  targetValueFormat: z.string().optional().nullable(),
  targetValueDataType: z.string().optional(),
});

const valueDimensionSchema = AlgorithmJoinItemBaseValidFormDataSchema.extend({
  _variant: z.literal('VALUE_DIMENSION'),
  sourceValue: z.union([
    z.string(errorMap(() => t('validation.source.value.too_small'))).min(1),
    z.number(),
  ]),
  sourceValueFormat: z.string().optional().nullable(),
  sourceValueDataType: z.string().optional(),
  targetDimension: zDimension('target'),
});

export const DANGLING_MAPPING_TOKEN = '_DANGLING_MAPPING_';

// Applying a workaround to a known issue with zod (https://github.com/colinhacks/zod/issues/2440)
// by first creating a regular union with some refined types and then piping it to a discriminated union
const AlgorithmJoinItemValidFormDataSchemaUnion = z.union([
  // source is NOT marked as "constant"
  // target is NOT marked as "constant"
  dimensionDimensionSchema.superRefine(({ sourceDimension }, ctx) => {
    if (sourceDimension.startsWith(DANGLING_MAPPING_TOKEN)) {
      const mappingId = Number(
        sourceDimension.split(DANGLING_MAPPING_TOKEN).pop(),
      );
      if (isNaN(mappingId) || !mappingId) {
        ctx.addIssue({
          code: 'custom',
          message: t('validation.source.algorithmMappingAlias.required'),
          path: ['algorithmMappingAlias'],
        });
      }
    }
  }),
  // source is NOT marked as "constant"
  // target is marked as "constant"
  dimensionValueSchema.superRefine(async (data, ctx) => {
    if (data.targetValueDataType) {
      await validateValueFormat(
        'target',
        data.targetValue,
        data.targetValueFormat,
        data.targetValueDataType,
        (message: string) =>
          ctx.addIssue({ code: 'custom', message, path: ['targetValue'] }),
      );
    } else {
      refineValue('target', ctx, data.targetValue);
    }
  }),
  // source is marked as "constant"
  // target is NOT marked as "constant"
  valueDimensionSchema.superRefine(async (data, ctx) => {
    if (data.sourceValueDataType) {
      await validateValueFormat(
        'source',
        data.sourceValue,
        data.sourceValueFormat,
        data.sourceValueDataType,
        (message: string) =>
          ctx.addIssue({ code: 'custom', message, path: ['sourceValue'] }),
      );
    } else {
      refineValue('source', ctx, data.sourceValue);
    }
  }),
  // source is NOT marked as "constant"
  // target is NOT marked as "constant"
  AlgorithmJoinItemBaseValidFormDataSchema.extend({
    _variant: z.literal('MAPPING_DIMENSION'),
    algorithmMappingAlias: zAlgorithmMappingAlias,
    targetDimension: zDimension('target'),
  }),
  // source is NOT marked as "constant"
  // target is marked as "constant"
  AlgorithmJoinItemBaseValidFormDataSchema.extend({
    _variant: z.literal('MAPPING_VALUE'),
    algorithmMappingAlias: zAlgorithmMappingAlias,
    targetValue: zValue('target'),
  }),
  // source is NOT marked as "constant"
  // target is NOT marked as "constant" — target is a mapping alias
  AlgorithmJoinItemBaseValidFormDataSchema.extend({
    _variant: z.literal('DIMENSION_MAPPING'),
    algorithmSourceId: zAlgorithmSourceId,
    sourceDimension: zDimension('source'),
    algorithmMappingAlias: zAlgorithmMappingAlias,
  }),
  // source is marked as "constant"
  // target is NOT marked as "constant" — target is a mapping alias
  AlgorithmJoinItemBaseValidFormDataSchema.extend({
    _variant: z.literal('VALUE_MAPPING'),
    sourceValue: zValue('source'),
    algorithmMappingAlias: zAlgorithmMappingAlias,
  }),
]);

export const AlgorithmJoinItemValidFormDataSchema =
  AlgorithmJoinItemValidFormDataSchemaUnion.pipe(
    z.discriminatedUnion('_variant', [
      dimensionDimensionSchema,
      dimensionValueSchema,
      valueDimensionSchema,
      AlgorithmJoinItemValidFormDataSchemaUnion.options[3],
      AlgorithmJoinItemValidFormDataSchemaUnion.options[4],
      AlgorithmJoinItemValidFormDataSchemaUnion.options[5],
      AlgorithmJoinItemValidFormDataSchemaUnion.options[6],
    ]),
  ).transform(objectWithRandomUniqueKey);

export const refineValue = (
  role: 'source' | 'target',
  ctx: RefinementCtx,
  value: string | number,
) => {
  if (typeof value !== 'string') {
    return;
  }

  try {
    zValue(role).parse(value);
  } catch (error) {
    (error as ZodError).issues.forEach((issue) =>
      ctx.addIssue({ ...issue, path: [`${role}Value`] }),
    );
  }
};

export const validateValueFormat = async (
  role: 'source' | 'target',
  value: string | number,
  format: string | null | undefined,
  dataType: string,
  addIssue: (message: string) => void,
) => {
  if (typeof value !== 'string') {
    return;
  }

  switch (dataType) {
    case 'DATE': {
      const { DATE_REGEX_ISO, isDateInFormat } = await import(
        '../../../../../common/utils/parsers/parseDate'
      );
      if (!isDateInFormat(value, DATE_REGEX_ISO)) {
        addIssue(t(`validation.${role}.value.must-be-in-date-format`));
      }
      break;
    }
    case 'NUMBER': {
      if (!isNumberInFormat(value)) {
        addIssue(t(`validation.${role}.value.must-be-a-number`));
      }
      break;
    }
    case 'TEXT': {
      const { isCodeInValidFormat } = await import(
        '../../../../../classifier/isCodeInValidFormat'
      );
      if (format && !isCodeInValidFormat(value, format)) {
        addIssue(
          t(`validation.${role}.value.does-not-match-classifier-code-format`, {
            format,
          }),
        );
      }
      break;
    }
  }
};
